import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabaseClient';
import NotificationBell from '../../components/admin/NotificationBell';
import MessageNotificationBell from '../../components/admin/MessageNotificationBell';

// Define the Team Member type to match our database structure
interface TeamMember {
  id: string;
  name: string;
  role: string;
  photo: string;
  bio: string;
  display_order: number;
  is_active: boolean;
  user_id?: string | null;
  created_at?: string;
  updated_at?: string;
}

interface TeamInvite {
  invite_code: string;
  team_id: string;
  expires_at: string;
}

// Toast notification type
interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error' | 'info';
}

// Toast component for non-intrusive notifications
const Toast = ({ toast, onClose }: { toast: Toast; onClose: () => void }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);
    
    return () => clearTimeout(timer);
  }, [onClose]);
  
  const bgColor = toast.type === 'success' 
    ? 'bg-green-500' 
    : toast.type === 'error' 
      ? 'bg-red-500' 
      : 'bg-blue-500';
  
  return (
    <div className={`${bgColor} text-white px-4 py-3 rounded shadow-lg flex justify-between items-center`}>
      <span>{toast.message}</span>
      <button 
        onClick={onClose}
        className="ml-4 text-white focus:outline-none"
      >
        ×
      </button>
    </div>
  );
};

const TeamManagement: React.FC = () => {
  // State variables
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentMember, setCurrentMember] = useState<TeamMember | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [generatedInvite, setGeneratedInvite] = useState<TeamInvite | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  
  // Add toast notification
  const addToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
  };
  
  // Remove toast notification
  const removeToast = (id: number) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  // Fetch data from Supabase
  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const { data, error } = await supabase
          .from('team')
          .select('*')
          .order('display_order', { ascending: true });
        
        if (error) {
          throw new Error(`Error fetching team members: ${error.message}`);
        }
        
        setTeamMembers(data || []);
      } catch (err: any) {
        console.error('Error fetching data:', err);
        setError(err.message || 'An error occurred while fetching data');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchTeamMembers();
  }, []);

  // Handler functions
  const handleAddMember = () => {
    setCurrentMember(null);
    setIsModalOpen(true);
  };
  
  const handleEditMember = (member: TeamMember) => {
    setCurrentMember(member);
    setIsModalOpen(true);
  };
  
  const handleDeleteMember = async (memberId: string) => {
    if (!window.confirm('Are you sure you want to delete this team member?')) {
      return;
    }
    
    try {
      setIsLoading(true);
      
      const { error } = await supabase
        .from('team')
        .delete()
        .match({ id: memberId });
      
      if (error) {
        throw new Error(`Error deleting team member: ${error.message}`);
      }
      
      // Update state
      setTeamMembers(teamMembers.filter(member => member.id !== memberId));
      
      // Show success toast
      addToast('Team member deleted successfully', 'success');
    } catch (err: any) {
      console.error('Error deleting team member:', err);
      // Show error toast
      addToast(`Failed to delete team member: ${err.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleToggleActive = async (member: TeamMember) => {
    try {
      setIsLoading(true);
      
      const { error } = await supabase
        .from('team')
        .update({ is_active: !member.is_active })
        .eq('id', member.id);
      
      if (error) {
        throw new Error(`Error updating team member status: ${error.message}`);
      }
      
      // Update state
      setTeamMembers(teamMembers.map(m => 
        m.id === member.id ? { ...m, is_active: !m.is_active } : m
      ));
      
      // Show success toast
      addToast(`Team member ${member.is_active ? 'hidden' : 'activated'} successfully`, 'success');
    } catch (err: any) {
      console.error('Error toggling active status:', err);
      // Show error toast
      addToast(`Failed to update status: ${err.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSaveMember = async (memberData: TeamMember) => {
    try {
      setIsLoading(true);
      
      if (currentMember) {
        // Update existing member
        const { data, error } = await supabase
          .from('team')
          .update({
            name: memberData.name,
            role: memberData.role,
            photo: memberData.photo,
            bio: memberData.bio,
            display_order: memberData.display_order,
            is_active: memberData.is_active
          })
          .eq('id', currentMember.id)
          .select();

        if (error) {
          throw new Error(`Error updating team member: ${error.message}`);
        }
        
        if (data && data.length > 0) {
          // Update state with returned data
          setTeamMembers(teamMembers.map(member => 
            member.id === currentMember.id ? data[0] : member
          ));
          
          // Show success toast
          addToast('Team member updated successfully', 'success');
        } else {
          // Fetch the updated team member to ensure it was updated
          const { data: refreshedData, error: refreshError } = await supabase
            .from('team')
            .select('*')
            .eq('id', currentMember.id)
            .single();
          
          if (refreshError) {
            throw new Error(`Error confirming team member update: ${refreshError.message}`);
          }
          
          if (refreshedData) {
            // Update state with refreshed data
            setTeamMembers(teamMembers.map(member => 
              member.id === currentMember.id ? refreshedData : member
            ));
            
            // Show success toast
            addToast('Team member updated successfully', 'success');
          } else {
            throw new Error('Failed to confirm team member update - member not found after update');
          }
        }
      } else {
        // Add new member
        // Remove the id field for new records - let Supabase generate it
        const { id, ...newMemberData } = memberData;
        
        const { data, error } = await supabase
          .from('team')
          .insert([newMemberData])
          .select();

        if (error) {
          throw new Error(`Error creating team member: ${error.message}`);
        }
        
        if (data && data.length > 0) {
          setTeamMembers([...teamMembers, data[0]]);
          
          // Show success toast
          addToast('Team member created successfully', 'success');
        } else {
          throw new Error('Failed to create team member - no data returned from insert operation');
        }
      }
      
      setIsModalOpen(false);
    } catch (err: any) {
      console.error('Error saving team member:', err);
      // Show error toast
      addToast(`Failed to save team member: ${err.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const generateInviteCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = 'CREW-';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  const handleGenerateInvite = async (teamId: string) => {
    try {
      setIsLoading(true);

      // Get current user ID
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('You must be logged in to generate invite codes');
      }

      // Generate invite code
      const inviteCode = generateInviteCode();

      // Set expiration to 30 days from now
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 30);

      // Insert invite into database
      const { data, error } = await supabase
        .from('team_invites')
        .insert({
          team_id: teamId,
          invite_code: inviteCode,
          created_by: user.id,
          expires_at: expiresAt.toISOString(),
          used: false
        })
        .select()
        .single();

      if (error) {
        throw new Error(`Error generating invite code: ${error.message}`);
      }

      setGeneratedInvite(data);
      setShowInviteModal(true);
      addToast('Invite code generated successfully!', 'success');
    } catch (err: any) {
      console.error('Error generating invite:', err);
      addToast(`Failed to generate invite: ${err.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const copyInviteCode = () => {
    if (generatedInvite) {
      navigator.clipboard.writeText(generatedInvite.invite_code);
      addToast('Invite code copied to clipboard!', 'success');
    }
  };

  // Loading state
  if (isLoading && teamMembers.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-[#B2D8C5] border-t-[#232347] rounded-full animate-spin mb-4"></div>
          <p className="text-[#232347]">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-2 relative">
      {/* Toast container */}
      <div className="fixed bottom-4 right-4 z-50 space-y-2">
        {toasts.map(toast => (
          <Toast 
            key={toast.id} 
            toast={toast} 
            onClose={() => removeToast(toast.id)} 
          />
        ))}
      </div>
      
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-semibold text-[#344854]">Team Management</h1>
        <div className="flex items-center gap-3">
          <MessageNotificationBell />
          <NotificationBell />
          <button
            className="bg-[#3A5E63] text-white px-4 py-2 rounded-md hover:bg-[#2a4548] flex items-center"
            onClick={handleAddMember}
          >
            <span className="mr-2">+</span> Add Team Member
          </button>
        </div>
      </div>
      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-6">
          {error}
        </div>
      )}
      
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Photo</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Account</th>
              <th scope="col" className="relative px-6 py-3">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {teamMembers.map((member) => (
              <tr key={member.id} className={`hover:bg-gray-50 ${!member.is_active ? 'bg-gray-50 text-gray-400' : ''}`}>
                <td className="px-6 py-4 whitespace-nowrap">
                  {member.photo ? (
                    <img 
                      src={member.photo} 
                      alt={member.name} 
                      className="h-10 w-10 object-cover rounded-full" 
                    />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-500">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{member.name}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-500">{member.role}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-500">{member.display_order}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${member.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                    {member.is_active ? 'Active' : 'Hidden'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {member.user_id ? (
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                      Linked
                    </span>
                  ) : (
                    <button
                      onClick={() => handleGenerateInvite(member.id)}
                      className="text-[#3A5E63] hover:text-[#2a4548] text-xs font-semibold"
                    >
                      Generate Invite
                    </button>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    onClick={() => handleToggleActive(member)}
                    className={`mr-3 ${member.is_active ? 'text-gray-600 hover:text-gray-900' : 'text-green-600 hover:text-green-900'}`}
                  >
                    {member.is_active ? 'Hide' : 'Activate'}
                  </button>
                  <button
                    onClick={() => handleEditMember(member)}
                    className="text-indigo-600 hover:text-indigo-900 mr-3"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteMember(member.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            
            {teamMembers.length === 0 && !isLoading && (
              <tr>
                <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500">
                  No team members found. Add your first team member!
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      {/* Team Member Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">
                {currentMember ? 'Edit Team Member' : 'Add New Team Member'}
              </h3>
              <button
                className="text-gray-400 hover:text-gray-500"
                onClick={() => setIsModalOpen(false)}
              >
                ✕
              </button>
            </div>
            
            <div className="p-6">
              <form className="space-y-4">
                <div>
                  <label htmlFor="member-name" className="block text-sm font-medium text-gray-700 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    id="member-name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    defaultValue={currentMember?.name || ''}
                  />
                </div>
                
                <div>
                  <label htmlFor="member-role" className="block text-sm font-medium text-gray-700 mb-1">
                    Role
                  </label>
                  <input
                    type="text"
                    id="member-role"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    defaultValue={currentMember?.role || ''}
                  />
                </div>
                
                <div>
                  <label htmlFor="member-photo" className="block text-sm font-medium text-gray-700 mb-1">
                    Photo URL
                  </label>
                  <input
                    type="text"
                    id="member-photo"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    defaultValue={currentMember?.photo || ''}
                    placeholder="https://example.com/photo.jpg"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Enter a valid image URL. For future: we'll add image upload functionality.
                  </p>
                </div>
                
                {currentMember?.photo && (
                  <div className="mt-2">
                    <p className="text-sm font-medium text-gray-700 mb-1">Current Photo:</p>
                    <img 
                      src={currentMember.photo} 
                      alt={currentMember.name} 
                      className="h-40 w-40 object-cover rounded-full mx-auto"
                    />
                  </div>
                )}
                
                <div>
                  <label htmlFor="member-bio" className="block text-sm font-medium text-gray-700 mb-1">
                    Bio
                  </label>
                  <textarea
                    id="member-bio"
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    defaultValue={currentMember?.bio || ''}
                    placeholder="Enter a short biography..."
                  ></textarea>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="member-order" className="block text-sm font-medium text-gray-700 mb-1">
                      Display Order
                    </label>
                    <input
                      type="number"
                      id="member-order"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      defaultValue={currentMember?.display_order || teamMembers.length + 1}
                      min="1"
                    />
                  </div>
                  
                  <div className="flex items-center h-full pt-8">
                    <input
                      type="checkbox"
                      id="member-active"
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      defaultChecked={currentMember ? currentMember.is_active : true}
                    />
                    <label htmlFor="member-active" className="ml-2 block text-sm text-gray-900">
                      Active (visible on website)
                    </label>
                  </div>
                </div>
              </form>
            </div>
            
            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end">
              <button
                type="button"
                className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 mr-3"
                onClick={() => setIsModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-[#3A5E63] hover:bg-[#2a4548] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3A5E63]"
                onClick={() => {
                  const nameEl = document.getElementById('member-name') as HTMLInputElement;
                  const roleEl = document.getElementById('member-role') as HTMLInputElement;
                  const photoEl = document.getElementById('member-photo') as HTMLInputElement;
                  const bioEl = document.getElementById('member-bio') as HTMLTextAreaElement;
                  const orderEl = document.getElementById('member-order') as HTMLInputElement;
                  const activeEl = document.getElementById('member-active') as HTMLInputElement;
                  
                  // Validation
                  if (!nameEl.value.trim()) {
                    alert('Please enter a name');
                    nameEl.focus();
                    return;
                  }
                  
                  if (!roleEl.value.trim()) {
                    alert('Please enter a role');
                    roleEl.focus();
                    return;
                  }
                  
                  if (!bioEl.value.trim()) {
                    alert('Please enter a bio');
                    bioEl.focus();
                    return;
                  }
                  
                  // Create team member data object
                  const memberData: TeamMember = {
                    id: currentMember?.id || '',
                    name: nameEl.value.trim(),
                    role: roleEl.value.trim(),
                    photo: photoEl.value.trim(),
                    bio: bioEl.value.trim(),
                    display_order: parseInt(orderEl.value) || (teamMembers.length + 1),
                    is_active: activeEl.checked
                  };
                  
                  // Save the team member
                  handleSaveMember(memberData);
                }}
              >
                {currentMember ? 'Update Team Member' : 'Create Team Member'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invite Code Modal */}
      {showInviteModal && generatedInvite && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">
                Invite Code Generated
              </h3>
              <button
                className="text-gray-400 hover:text-gray-500"
                onClick={() => {
                  setShowInviteModal(false);
                  setGeneratedInvite(null);
                }}
              >
                ✕
              </button>
            </div>

            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">
                Share this invite code with the crew member to allow them to create their account and link to their team profile.
              </p>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                <p className="text-xs text-gray-500 mb-1">Invite Code:</p>
                <p className="text-2xl font-mono font-bold text-[#3A5E63] break-all">
                  {generatedInvite.invite_code}
                </p>
              </div>

              <div className="text-xs text-gray-500 mb-4">
                <p>Expires: {new Date(generatedInvite.expires_at).toLocaleDateString()}</p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={copyInviteCode}
                  className="flex-1 bg-[#3A5E63] text-white px-4 py-2 rounded-md hover:bg-[#2a4548] transition-colors"
                >
                  Copy Code
                </button>
                <button
                  onClick={() => {
                    setShowInviteModal(false);
                    setGeneratedInvite(null);
                  }}
                  className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamManagement; 